/**
 * External dependencies
 */
import clsx from 'clsx';

/**
 * WordPress dependencies
 */
import {
	ToggleGroupControl,
	ToggleGroupControlOptionIcon,
} from '@safe-wordpress/components';
import { _x } from '@safe-wordpress/i18n';
import {
	alignLeft,
	alignCenter,
	alignRight,
	alignJustify,
} from '@safe-wordpress/icons';
import { useMemo } from '@safe-wordpress/element';

const TEXT_ALIGNMENT_OPTIONS = [
	{
		label: _x( 'Align text left', 'text', 'nelio-ab-testing' ),
		value: 'left',
		icon: alignLeft,
	},
	{
		label: _x( 'Align text center', 'text', 'nelio-ab-testing' ),
		value: 'center',
		icon: alignCenter,
	},
	{
		label: _x( 'Align text right', 'text', 'nelio-ab-testing' ),
		value: 'right',
		icon: alignRight,
	},
	{
		label: _x( 'Justify text', 'text', 'nelio-ab-testing' ),
		value: 'justify',
		icon: alignJustify,
	},
];

const DEFAULT_OPTIONS = [ 'left', 'center', 'right' ];

/**
 * Control to facilitate text alignment selections.
 *
 * @param {Object}   props           Component props.
 * @param {string}   props.className Class name to add to the control.
 * @param {string}   props.value     Currently selected text alignment.
 * @param {Function} props.onChange  Handles change in text alignment selection.
 * @param {string[]} props.options   Array of text alignment options to display.
 *
 * @return {Element} Text alignment control.
 */
type TextAlignmentControlProps = {
	readonly className?: string;
	readonly value?: string | number;
	readonly onChange: ( value?: string | number ) => void;
	readonly options?: ReadonlyArray< string >;
	readonly disabled?: boolean;
};
export function TextAlignmentControl( {
	className,
	value,
	disabled,
	options = DEFAULT_OPTIONS,
	onChange,
}: TextAlignmentControlProps ): JSX.Element | null {
	const validOptions = useMemo(
		() =>
			TEXT_ALIGNMENT_OPTIONS.filter( ( option ) =>
				options.includes( option.value )
			),
		[ options ]
	);

	if ( ! validOptions.length ) {
		return null;
	}

	return (
		<ToggleGroupControl
			isDeselectable
			__nextHasNoMarginBottom
			__next40pxDefaultSize
			label={ _x( 'Text alignment', 'text', 'nelio-ab-testing' ) }
			className={ clsx(
				'block-editor-text-alignment-control',
				className
			) }
			value={ value }
			onChange={ ( newValue ) => {
				onChange( newValue === value ? undefined : newValue );
			} }
		>
			{ validOptions.map( ( option ) => {
				return (
					<ToggleGroupControlOptionIcon
						key={ option.value }
						value={ option.value }
						icon={ option.icon }
						label={ option.label }
						disabled={ disabled }
					/>
				);
			} ) }
		</ToggleGroupControl>
	);
}
